// @flow
import React from 'react';
import { H3 } from '@finect/tabular-core/Headings';
import { Paragraph } from '@finect/tabular-core/Paragraph';
import { ArticleDetail, ArticleSide, Video, ArticleCTA } from '../../../components';

type Props = {
  staticContext?: Object,
  location: Object,
  history: Object,
  toggleModal: Function
};

export const Herencia = ({ location, history, toggleModal }: Props) => {
  const redirectTo = (url: string) => {
    history.push(url);
  };

  return (
    <ArticleDetail
      location={location}
      title="Cómo puede ayudarte un asesor a gestionar una herencia y otros ingresos extra."
      redirectTo={redirectTo}
      toggleModal={toggleModal}
    >
      <Paragraph>
        Cuando fallece un ser querido, no sólo sentimos el dolor de su pérdida, sino que debemos enfrentarnos a muchas
        decisiones en las que nos habíamos parado a pensar, muchas de ellas financieras. La más importante, la herencia.
      </Paragraph>
      <Paragraph>
        De pronto, tenemos en nuestras manos una cartera que no sabemos cómo gestionar; o pasamos a ser propietarios de
        unos inmuebles con los que no contábamos; o quizá nos enfrentemos a unas deudas imprevistas… Todo, además, con
        profundas implicaciones fiscales que no suelen ser fáciles de entender y solventar.
      </Paragraph>
      <Paragraph>
        Es normal tener dudas y sentirse superado en estas situaciones complejas donde hay que tomar decisiones
        financieras relevantes en muy poco tiempo. Y es precisamente aquí donde más partido se puede sacar a la figura
        de un asesor financiero.
      </Paragraph>
      <Paragraph>Su papel aquí será muy clave y puede ayudarnos en varios puntos decisivos.</Paragraph>
      <H3>A tomar mejores decisiones.</H3>
      <Paragraph>
        El asesor nos ayudará a poner todo en perspectiva y a ver qué opciones tenemos para esa herencia. Así podremos
        tomar una decisión más informada.
      </Paragraph>
      <ArticleSide full position="right">
        <Video
          id="0TT6oHIiB3Q"
          src="https://www.youtube.com/embed/0TT6oHIiB3Q?hd=1&showinfo=0&controls=0&rel=0&enablejsapi=1"
          caption="Máximo Pérez, agente financiero."
        />
      </ArticleSide>
      <Paragraph>
        Desde qué parte debemos mantener en liquidez y cuál debemos invertir a largo plazo; si compensa alquilar la casa
        heredada o ponerla a la venta; si es mejor vender los fondos de inversión o cambiarlos a otros, qué hacer con
        los seguros o cómo aprovecharlos para pagar los impuestos por la herencia.
      </Paragraph>
      <Paragraph>
        Un conjunto de asuntos en los que nos jugamos muchos miles de euros con los que la ayuda de un buen profesional
        puede ser más que rentable. Si queremos llevar el asunto nosotros mismos, sin ayuda, nos exigiría dedicar buena
        parte de nuestro tiempo a formarnos en estas materias para no meter la pata fácilmente.
      </Paragraph>
      <H3>A ganar tranquilidad fiscal.</H3>
      <Paragraph>
        El asesor también te permitirá despreocuparte o no tener que destinar tanto tiempo para analizar las
        implicaciones fiscales de la herencia o los efectos en el impuesto de sucesiones y en la declaración de la renta
        del siguiente año.
      </Paragraph>
      <Paragraph>
        No es sencillo, sobre todo cuando la herencia incluye diversos activos y vivimos en un lugar diferente al
        fallecido, por ejemplo. Y, si lo hacemos mal, nos arriesgamos encima a una potencial multa de Hacienda.
      </Paragraph>
      <Paragraph>
        Es clave aquí contar con la figura del profesional que nos dé la tranquilidad de estar haciendo bien las cosas,
        sin pagar de más.
      </Paragraph>
      <H3>A tomar decisiones más racionales.</H3>
      <Paragraph>
        Por último, y no menos importante, nos dará tranquilidad y racionalidad en momentos de emociones tan profundas
        que nos pueden llevar a tomar decisiones equivocadas, que pueden acabar siendo muy perjudiciales a largo plazo
        para nuestra salud financiera. En este sentido, puede resolver dudas sobre ese dinero que ayuden a evitar
        problemas familiares.
      </Paragraph>
      <Paragraph>
        Por eso, los más precavidos y mejor organizados, además de dedicar tiempo a hacer un buen testamento, cuentan
        con el respaldo de un asesor que les ayude a diseñar la forma de repartir entre sus herederos la herencia que
        les dejará.
      </Paragraph>
      <Paragraph>
        Así, cuando llegue el momento, este profesional proporcionará tranquilidad a la familia con todos estos asuntos
        financieros, para los que nadie tiene la cabeza en momentos tan complejos.
      </Paragraph>
      <ArticleCTA toggleModal={toggleModal} />
      <H3>También para indemnizaciones, premios o venta de la empresa.</H3>
      <Paragraph>
        La herencia es sólo uno de los momentos en los que recibimos un dinero extra. Algo parecido nos sucede cuando
        recibimos una buena oferta por nuestro negocio. De un día para otro, afrontamos la necesidad de decidir si es
        una propuesta oferta o no, pensar qué hacer después con la suma de dinero que obtendremos de golpe, cómo debemos
        estructurar la operación para no pagar de más…
      </Paragraph>
      <Paragraph>
        También ocurre lo mismo con los premios si, por ejemplo, nos toca la lotería o si recibimos una indemnización
        por despido. Son sólo tres ejemplos, pero en todos ellos un asesor financiero puede ayudarnos a tomar mejores
        decisiones.
      </Paragraph>
    </ArticleDetail>
  );
};
